import {
  Alert,
  Box,
  Button,
  MobileStepper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  clearDonorInfo,
  loadDonorInfo,
  pickDonorFields,
  saveDonorInfo,
} from '@/utils/donorStorage';

import StepAmount from './StepAmount';
import StepBidwars from './StepBidwars';
import StepConfirmation from './StepConfirmation';
import StepPayment from './StepPayment';
import StepReceiptInfo from './StepReceiptInfo';
import StepReview from './StepReview';

const steps = [StepAmount, StepBidwars, StepReceiptInfo, StepReview, StepPayment, StepConfirmation];

const defaultFormData = {
  amount: '',
  username: '',
  message: '',
  isAnonymous: false,
  userEmail: '',
  donorType: 'person',
  userFullName: '',
  userCompany: '',
  userCity: '',
  userAddress: '',
  userProvince: '',
  userPostalCode: '',
  userCountry: '',
  bidAllocations: [],
};

export default function DonationForm({ eventId }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm')); // bascule mobile vs desktop

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(defaultFormData);
  const [chargeId, setChargeId] = useState(null);
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const [confirmationInfo, setConfirmationInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [isCreatingCharge, setIsCreatingCharge] = useState(false);
  // Whether the donor asked us to remember their information on this device
  const [shouldSaveInfo, setShouldSaveInfo] = useState(false);
  // Whether this form was filled from a previous donation, to notify the donor
  const [isPrefilled, setIsPrefilled] = useState(false);
  // Saved information is only readable client-side, so the first render can't use it
  const [hasLoadedSavedInfo, setHasLoadedSavedInfo] = useState(false);

  const stepLabels = [
    t('donation.stepAmount'),
    t('donation.stepBidwars'),
    t('donation.stepReceipt'),
    t('donation.stepReview'),
    t('donation.stepPayment'),
    t('donation.stepConfirm'),
  ];

  const nextStep = useCallback(
    () => setCurrentStep(prev => Math.min(prev + 1, steps.length - 1)),
    [],
  );
  const PAYMENT_STEP_INDEX = steps.indexOf(StepPayment);

  const clearPaymentState = useCallback(() => {
    setPaymentStatus(null);
    setErrorMessage('');
    setChargeId(null);
    setPaypalOrderId(null);
    setIsCreatingCharge(false);
  }, []);

  const prevStep = useCallback(() => {
    if (currentStep === steps.length - 1) return; // Prevent going back from confirmation step
    if (currentStep === PAYMENT_STEP_INDEX) clearPaymentState();
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, [currentStep, PAYMENT_STEP_INDEX, clearPaymentState]);

  // Prefill from a previous donation. Runs once, after hydration.
  useEffect(() => {
    const savedInfo = loadDonorInfo();
    if (savedInfo) {
      setFormData(prev => ({ ...prev, ...savedInfo }));
      setShouldSaveInfo(true);
      setIsPrefilled(true);
    }
    setHasLoadedSavedInfo(true);
  }, []);

  // Keep storage in sync, so going back to fix a typo updates what we remember
  useEffect(() => {
    if (!hasLoadedSavedInfo) return; // Never clear before we've had a chance to read
    if (shouldSaveInfo) {
      saveDonorInfo(formData);
    } else {
      clearDonorInfo();
    }
  }, [hasLoadedSavedInfo, shouldSaveInfo, formData]);

  const clearSavedInfo = useCallback(() => {
    setShouldSaveInfo(false); // The effect above removes the stored information
    setIsPrefilled(false);
    setFormData(prev => ({ ...prev, ...pickDonorFields(defaultFormData) }));
  }, []);

  /** Clear the donation, keeping the donor's information if they asked us to. */
  const clearFormData = useCallback(
    () =>
      setFormData(prev =>
        shouldSaveInfo ? { ...defaultFormData, ...pickDonorFields(prev) } : defaultFormData,
      ),
    [shouldSaveInfo],
  );

  useEffect(() => {
    if (isPaymentComplete) {
      clearFormData();
      setCurrentStep(steps.length - 1); // Go to confirmation step
    }
  }, [isPaymentComplete, clearFormData]);

  const resetForm = () => {
    clearFormData();
    setIsPrefilled(shouldSaveInfo); // The next donation starts off prefilled
    setChargeId(null);
    setPaypalOrderId(null);
    setConfirmationInfo(null);
    setPaymentStatus(null);
    setErrorMessage('');
    setIsPaymentComplete(false);
    setIsCreatingCharge(false);
    setCurrentStep(0);
  };

  const StepComponent = steps[currentStep];

  return (
    <Box sx={{ width: '100%', marginTop: 4 }}>
      {isSmall ? (
        // --- tiny stepper for mobile ---
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: 3 }}
        >
          <Typography variant="body2">
            {t('donation.step')} {currentStep + 1}/{steps.length} - {stepLabels[currentStep]}
          </Typography>

          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={currentStep}
            nextButton={null}
            backButton={null}
            sx={{ padding: 0, bgcolor: 'transparent' }}
          />
        </Stack>
      ) : (
        // --- regular stepper for desktop ---
        <Stepper alternativeLabel activeStep={currentStep} sx={{ marginBottom: 4 }}>
          {stepLabels.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  if (index < currentStep && currentStep !== steps.length - 1) {
                    if (currentStep === PAYMENT_STEP_INDEX) clearPaymentState();
                    setCurrentStep(index);
                  }
                }}
                sx={[
                  index < currentStep && currentStep !== steps.length - 1 && { cursor: 'pointer' },
                  index > currentStep && { opacity: 0.5 },
                ]}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {isPrefilled && currentStep === 0 && (
        <Alert
          severity="info"
          sx={{ marginBottom: 3 }}
          action={
            <Button size="small" color="inherit" onClick={clearSavedInfo}>
              {t('common.clear')}
            </Button>
          }
        >
          {t('donation.usingSavedInfo')}
        </Alert>
      )}

      <StepComponent
        formData={formData}
        setFormData={setFormData}
        shouldSaveInfo={shouldSaveInfo}
        setShouldSaveInfo={setShouldSaveInfo}
        nextStep={nextStep}
        prevStep={prevStep}
        setCurrentStep={setCurrentStep}
        eventId={eventId}
        chargeId={chargeId}
        setChargeId={setChargeId}
        paypalOrderId={paypalOrderId}
        setPaypalOrderId={setPaypalOrderId}
        confirmationInfo={confirmationInfo}
        setConfirmationInfo={setConfirmationInfo}
        paymentStatus={paymentStatus}
        setPaymentStatus={setPaymentStatus}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isPaymentComplete={isPaymentComplete}
        setIsPaymentComplete={setIsPaymentComplete}
        isCreatingCharge={isCreatingCharge}
        setIsCreatingCharge={setIsCreatingCharge}
        resetForm={resetForm}
      />
    </Box>
  );
}

DonationForm.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
